import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CircularProgress, Stack } from "@mui/joy";
import DefaultPage from "pages/container/DefaultPage";
import useLoginPromotion from "hooks/useLoginPromotion";

// 카카오 인가 코드 콜백 처리 (퍼널 공통 로직).
// 퍼널 구분은 라우트가 한다 — 프로모션/제휴가 서로 다른 경로로 돌아오며,
// 각 라우트 컴포넌트가 아래 3개 값을 정해서 넘긴다.
const KakaoOAuthCallback = ({
  callbackPath,
  referralCode,
  fallbackPath,
}: {
  // 이 콜백의 경로. 카카오 토큰 교환 시 authorize 때와 동일한 redirectUri 여야 한다.
  callbackPath: string;
  // 제휴 진입이면 레퍼럴 코드, 프로모션이면 undefined.
  referralCode?: string;
  // 취소/실패 시 돌아갈 로그인 화면.
  fallbackPath: string;
}) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const code = searchParams.get("code");
  const rawState = searchParams.get("state");
  const hasCalledLogin = useRef(false);

  const { login } = useLoginPromotion({
    url: `/user/v2/login`,
    code: code!!,
    type: "kakao",
    redirectUri: `${window.location.origin}${callbackPath}`,
    referralCode: referralCode,
  });

  useEffect(() => {
    if (!code) {
      console.error("[Kakao Login] Authorization code가 없습니다.");
      navigate(fallbackPath, { replace: true });
      return;
    }

    // 동일 인가 코드의 중복 제출 방지 (KOE320 방지).
    // useRef 는 컴포넌트 리마운트/동시 마운트 시 초기화되므로,
    // 코드 값 기준으로 sessionStorage 에 1회 제출 플래그를 둔다.
    const guardKey = `kakao_oauth_code_used:${code}`;
    if (hasCalledLogin.current || sessionStorage.getItem(guardKey)) return;

    // 즉시 플래그를 설정하여 동시 실행 방지
    hasCalledLogin.current = true;
    sessionStorage.setItem(guardKey, "1");

    // Authorization code를 사용한 후 URL에서 즉시 제거하여
    // 새로고침/뒤로가기 시 재사용 방지.
    // state 는 남긴다 — 재사용 위험이 없고, 제휴 콜백에서는 이게 레퍼럴 코드라
    // 지우면 새로고침 시 귀속이 끊긴다.
    window.history.replaceState(
      {},
      document.title,
      rawState
        ? `${callbackPath}?state=${encodeURIComponent(rawState)}`
        : callbackPath
    );

    // 로그인 실패(예: 만료/무효 code 로 인한 KOE320) 시 무한 로딩 대신
    // 로그인 페이지로 돌려보내 재시도(새 code 발급)할 수 있게 한다.
    login().catch(() => {
      navigate(fallbackPath, { replace: true });
    });
    // login과 navigate를 의존성에서 제거하여 불필요한 재실행 방지
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  return (
    <DefaultPage
      content={
        <Stack alignItems={"center"}>
          <CircularProgress color="neutral" />
        </Stack>
      }
    />
  );
};

export default KakaoOAuthCallback;
